import { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

// Tiempo en milisegundos tras el que se muestra el botón de reinicio.
const SHOW_GIVE_UP_AFTER_MS = 30_000;

interface ResumingSessionScreenProps {
  // Callback para que el usuario abandone la espera e inicie un proceso nuevo desde cero.
  onGiveUp?: () => void;
}

// Pantalla de espera para el estado RESUMING_SESSION (D-8).
// Se muestra cuando D-7 detecta que el usuario tiene un proceso in_progress.
// El frontend sondea getUserKycStatus hasta que el pipeline server-side resuelve.
export function ResumingSessionScreen({ onGiveUp }: ResumingSessionScreenProps) {
  const [showGiveUp, setShowGiveUp] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowGiveUp(true);
    }, SHOW_GIVE_UP_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        px: 3,
        gap: 2,
        textAlign: 'center',
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
        <CircularProgress
          size={64}
          thickness={2.5}
          sx={{ color: 'warning.main' }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <HourglassEmptyIcon sx={{ fontSize: 28, color: 'warning.main' }} />
        </Box>
      </Box>

      <Typography variant="h6" fontWeight={600}>
        Tu verificación está en proceso
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
        Estamos procesando tu documento. El resultado estará listo en unos momentos.
      </Typography>

      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ maxWidth: 280, lineHeight: 1.6 }}
      >
        Puedes cerrar esta ventana — tu proceso continuará en segundo plano y
        podrás regresar en cualquier momento para ver el resultado.
      </Typography>

      {showGiveUp && onGiveUp && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={onGiveUp}
            sx={{ fontSize: '0.8125rem' }}
          >
            Iniciar verificación desde cero
          </Button>
          <Typography variant="caption" color="text.disabled" sx={{ maxWidth: 260 }}>
            Esto cancela la espera e inicia un proceso nuevo. El resultado del
            proceso actual se descartará si aún no terminó.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
